import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { Praxis, Zuweisung, Zeitslot, Abwesenheit, Feiertag } from '../types';
import { formatDate, isSameDay, isDateWithin } from './dateUtils';

interface PDFExportOptions {
  weekStart: Date;
  praxen: Praxis[];
  zuweisungen: Zuweisung[];
  abwesenheiten: Abwesenheit[];
  feiertage?: Feiertag[];
  includeEmptyCells?: boolean;
  logoUrl?: string;
  appTitle?: string;
  filenamePrefix?: string;
}

export const getWeekNumber = (date: Date): number => {
  const d = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
  const dayNum = d.getUTCDay() || 7;
  d.setUTCDate(d.getUTCDate() + 4 - dayNum);
  const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
  return Math.ceil((((d.getTime() - yearStart.getTime()) / 86400000) + 1) / 7);
};

const loadImageAsBase64 = (url: string): Promise<string> => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'Anonymous';
    img.onload = () => {
      const canvas = document.createElement('canvas');
      canvas.width = img.width;
      canvas.height = img.height;
      const ctx = canvas.getContext('2d');
      if (ctx) {
        ctx.drawImage(img, 0, 0);
        resolve(canvas.toDataURL('image/png'));
      } else {
        reject(new Error('Failed to get canvas context'));
      }
    };
    img.onerror = reject;
    img.src = url;
  });
};

export const exportWeeklyScheduleToPDF = async (options: PDFExportOptions): Promise<{ doc: jsPDF; filename: string }> => {
  const { weekStart, praxen, zuweisungen, abwesenheiten, feiertage = [], includeEmptyCells = true, logoUrl = '/logo_praxis_hayat_seite.webp', appTitle = 'Dienstplan', filenamePrefix = '' } = options;

  // Create PDF in landscape mode
  const doc = new jsPDF({
    orientation: 'landscape',
    unit: 'mm',
    format: 'a4',
  });

  // Calculate week end
  const weekEnd = new Date(weekStart);
  weekEnd.setDate(weekEnd.getDate() + 5);

  // Get week number and year
  const weekNumber = getWeekNumber(weekStart);
  const year = weekStart.getFullYear();

  // Try to load and add logo
  try {
    const logoBase64 = await loadImageAsBase64(logoUrl);
    // Add logo in top left corner (20mm from left, 10mm from top, 40mm wide, auto height)
    doc.addImage(logoBase64, 'PNG', 20, 8, 40, 0);
  } catch (error) {
    console.warn('Could not load logo for PDF:', error);
  }

  // Add header (adjusted position to account for logo)
  doc.setFontSize(16);
  doc.text(appTitle, 148, 15, { align: 'center' });

  doc.setFontSize(12);
  doc.text(`KW ${weekNumber} - ${year}`, 148, 22, { align: 'center' });
  doc.text(`${formatDate(weekStart)} - ${formatDate(weekEnd)}`, 148, 28, { align: 'center' });

  // Prepare table data
  const weekDays = ['Montag', 'Dienstag', 'Mittwoch', 'Donnerstag', 'Freitag', 'Samstag'];

  // Helper to check if a day has an afternoon slot
  const hasAfternoonSlot = (dayIndex: number) => {
    // 0=Mon, 1=Tue, 2=Wed, 3=Thu, 4=Fri, 5=Sat
    // No afternoon for Wed (2), Fri (4), Sat (5)
    return ![2, 4, 5].includes(dayIndex);
  };

  // Create header row with day names
  const headerRow1: any[] = ['Praxis'];
  weekDays.forEach((day, index) => {
    const date = new Date(weekStart);
    date.setDate(date.getDate() + index);
    const colSpan = hasAfternoonSlot(index) ? 2 : 1;
    headerRow1.push({ content: `${day}\n${formatDate(date)}`, colSpan, styles: { halign: 'center' } });
  });

  // Create sub-header row with VM/NM and track column types for styling
  const headerRow2: string[] = [''];
  const columnTypes: string[] = ['PRAXIS']; // 0 is Praxis

  weekDays.forEach((_, index) => {
    headerRow2.push('VM');
    columnTypes.push('VM');

    if (hasAfternoonSlot(index)) {
      headerRow2.push('NM');
      columnTypes.push('NM');
    }
  });

  // Helper function to check if a doctor is absent on a given date
  const isArztAbsent = (arztId: number, date: Date): boolean => {
    return abwesenheiten.some((a) => a.arztId === arztId && isDateWithin(date, a.startDatum, a.endDatum));
  };

  // Helper function to get assignments for a cell (excluding absent doctors, sorted alphabetically)
  const getZuweisungenForCell = (praxisId: number, date: Date, zeitslot: Zeitslot): Zuweisung[] => {
    return zuweisungen.filter(
      (z) =>
        z.praxisId === praxisId &&
        isSameDay(z.datum, date) &&
        z.zeitslot === zeitslot &&
        !isArztAbsent(z.arztId, date) // Exclude absent doctors
    ).sort((a, b) => {
      const nameA = a.arzt ? `${a.arzt.nachname} ${a.arzt.vorname}` : '';
      const nameB = b.arzt ? `${b.arzt.nachname} ${b.arzt.vorname}` : '';
      return nameA.localeCompare(nameB);
    });
  };

  // Create body rows
  const bodyRows = praxen.map((praxis) => {
    const row: any[] = [praxis.name];

    weekDays.forEach((_, dayIndex) => {
      const date = new Date(weekStart);
      date.setDate(date.getDate() + dayIndex);

      // Helper to check holiday
      const getHoliday = (d: Date, slot: Zeitslot) => {
        return feiertage.find(f => {
          if (!isSameDay(f.datum, d)) return false;
          return f.typ === 'GANZTAGS' || f.typ === slot;
        });
      };

      // Check for full day holiday
      const fullDayHoliday = getHoliday(date, 'GANZTAGS' as any);

      const holidayStyle = {
        fillColor: [255, 237, 213] as [number, number, number], // bg-orange-100
        textColor: [154, 52, 18] as [number, number, number],   // text-orange-800
        valign: 'middle' as 'middle',
        halign: 'center' as 'center'
      };

      if (fullDayHoliday) {
        // Push merged cell
        row.push({
          content: fullDayHoliday.name || 'FEIERTAG',
          colSpan: hasAfternoonSlot(dayIndex) ? 2 : 1,
          styles: holidayStyle,
          _isHoliday: true // Flag for didParseCell
        });

        // Skip specific slots
        return;
      }

      // Vormittag cell
      const vmHoliday = getHoliday(date, Zeitslot.VORMITTAG);
      if (vmHoliday) {
        row.push({ content: vmHoliday.name || 'FEIERTAG', styles: holidayStyle, _isHoliday: true });
      } else {
        const vmZuweisungen = getZuweisungenForCell(praxis.id, date, Zeitslot.VORMITTAG);
        const vmText = vmZuweisungen.length > 0
          ? vmZuweisungen.map(z => z.arzt ? `${z.arzt.vorname} ${z.arzt.nachname}` : 'Unbekannt').join('\n')
          : (includeEmptyCells ? '-' : '');
        row.push(vmText);
      }

      // Nachmittag cell
      if (hasAfternoonSlot(dayIndex)) {
        const nmHoliday = getHoliday(date, Zeitslot.NACHMITTAG);
        if (nmHoliday) {
          row.push({ content: nmHoliday.name || 'FEIERTAG', styles: holidayStyle, _isHoliday: true });
        } else {
          const nmZuweisungen = getZuweisungenForCell(praxis.id, date, Zeitslot.NACHMITTAG);
          const nmText = nmZuweisungen.length > 0
            ? nmZuweisungen.map(z => z.arzt ? `${z.arzt.vorname} ${z.arzt.nachname}` : 'Unbekannt').join('\n')
            : (includeEmptyCells ? '-' : '');
          row.push(nmText);
        }
      }
    });

    return row;
  });

  // Generate table
  autoTable(doc, {
    head: [headerRow1, headerRow2],
    body: bodyRows,
    startY: 35,
    theme: 'grid',
    styles: {
      fontSize: 8,
      cellPadding: 2,
      overflow: 'linebreak',
      valign: 'middle',
    },
    headStyles: {
      fillColor: [229, 231, 235], // gray-200
      textColor: [31, 41, 55], // gray-800
      fontStyle: 'bold',
      halign: 'center',
    },
    columnStyles: {
      0: { cellWidth: 35, fontStyle: 'bold', halign: 'left' }, // Praxis column
    },
    didParseCell: (data) => {
      const colType = columnTypes[data.column.index];
      const cellContent = data.cell.raw as any;

      const isHoliday = cellContent && cellContent._isHoliday === true;

      // If it's a holiday, we've already set the styles in the body row definition.
      // We explicitly PRESERVE those styles and DO NOT apply the default column coloring.
      if (isHoliday) {
        return;
      }

      // Color code VM (Vormittag) cells with blue background
      if (data.section === 'body' && colType === 'VM') {
        data.cell.styles.fillColor = [219, 234, 254]; // blue-100
      }
      // Color code NM (Nachmittag) cells with purple background
      if (data.section === 'body' && colType === 'NM') {
        data.cell.styles.fillColor = [243, 232, 255]; // purple-100
      }
      // Color code header VM cells
      if (data.section === 'head' && data.row.index === 1 && colType === 'VM') {
        data.cell.styles.fillColor = [191, 219, 254]; // blue-300
        data.cell.styles.textColor = [30, 64, 175]; // blue-800
      }
      // Color code header NM cells
      if (data.section === 'head' && data.row.index === 1 && colType === 'NM') {
        data.cell.styles.fillColor = [233, 213, 255]; // purple-300
        data.cell.styles.textColor = [107, 33, 168]; // purple-800
      }
    },
  });

  // Add legend at the bottom - small, right-aligned, one row
  const finalY = (doc as any).lastAutoTable.finalY || 35;
  const pageWidth = doc.internal.pageSize.width;
  doc.setFontSize(7);
  doc.setTextColor(100, 100, 100); // gray
  doc.text('VM = Vormittag (08:00-13:00)  |  NM = Nachmittag (13:00-18:00)', pageWidth - 20, finalY + 5, { align: 'right' });

  // Add Abwesenheiten / Urlaub - closer to table
  if (abwesenheiten && abwesenheiten.length > 0) {
    doc.setTextColor(185, 28, 28); // red-700
    doc.setFontSize(10);

    doc.text('Urlaub / Abwesenheiten:', 20, finalY + 10);

    doc.setFontSize(9);
    let yPos = finalY + 16;
    let xPos = 25;
    const margin = 20;
    const maxWidth = pageWidth - margin;

    abwesenheiten.forEach((abwesenheit, index) => {
      const arztName = abwesenheit.arzt
        ? `${abwesenheit.arzt.vorname} ${abwesenheit.arzt.nachname}`
        : 'Unbekannt';
      const dateRange = `${formatDate(new Date(abwesenheit.startDatum))} - ${formatDate(new Date(abwesenheit.endDatum))}`;
      // Add separator if not the first item
      const prefix = index > 0 ? '   |   ' : '';
      const text = `${prefix}${arztName} (${abwesenheit.typ}: ${dateRange})`;

      const textWidth = doc.getTextWidth(text);

      // Check if text fits on current line
      if (xPos + textWidth > maxWidth) {
        yPos += 5; // Line height 5
        xPos = 25;
      }

      // Check if we need a new page
      if (yPos > 200) { // Maximize page usage (approx 10mm bottom margin)
        // Actually, if we hit bottom, new page.
        if (yPos > 195) {
          doc.addPage();
          yPos = 15;
          xPos = 25;
        }
      }

      doc.text(text, xPos, yPos);
      xPos += textWidth;
    });
  }

  // Generate filename
  const prefix = filenamePrefix ? `${filenamePrefix}_` : '';
  const filename = `${prefix}Dienstplan_KW${weekNumber}_${year}.pdf`;

  // Return PDF instead of saving
  return { doc, filename };
};
